import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import type * as api from '@gitbook/api-client';

import { getSiteSpacesFromStructure } from '../../state/sites/structure';
import type { LocalizationConfig, LocalizedLanguagesList } from './types';

/**
 * Returns localization config based on all languages used across the entire site structure.
 * This enables localization for all sections/groups if any part of the site has multiple languages.
 */
export function useSiteStructureLocalization(siteStructure: api.SiteStructure): LocalizationConfig {
    const localizedTitleFlag = useFlag('LOCALIZED_SITE_SECTION_TITLE');

    // Get all languages used across the entire site structure
    const usedLanguagesSet = React.useMemo(
        () =>
            new Set(
                getSiteSpacesFromStructure(siteStructure)
                    .map((s) => s.space.language)
                    .filter((lang): lang is api.TranslationLanguage => Boolean(lang))
            ),
        [siteStructure]
    );

    return {
        enabled: usedLanguagesSet.size > 1 && localizedTitleFlag.isEnabled,
        sortLanguages: (languages) => sortLanguagesByUsage(languages, usedLanguagesSet),
    };
}

/**
 * Sort languages by usage, putting the ones used in the site structure first.
 */
function sortLanguagesByUsage(
    languages: LocalizedLanguagesList,
    usedLanguagesSet: Set<api.TranslationLanguage | undefined>
) {
    return Object.fromEntries(
        Object.entries(languages).sort((a, b) => {
            const aUsed = usedLanguagesSet.has(a[0] as api.TranslationLanguage);
            const bUsed = usedLanguagesSet.has(b[0] as api.TranslationLanguage);
            return aUsed === bUsed ? 0 : aUsed ? -1 : 1;
        })
    ) as LocalizedLanguagesList;
}
